/**
 * NOS-159: client for the stock-images edge function (Unsplash proxy).
 * Search results are ephemeral; selecting a photo ingests it — the server
 * fires Unsplash's download ping, downloads the bytes, and stores them in the
 * org's image storage. Only the returned storage URL is ever persisted.
 */
import { supabase } from "@/lib/supabase"

export interface StockPhoto {
  id: string
  alt: string
  thumb_url: string
  preview_url: string
  width: number
  height: number
  download_location: string
  photographer_name: string
  photographer_url: string
}

export interface StockSearchResult {
  total: number
  total_pages: number
  page: number
  results: StockPhoto[]
}

export interface IngestedStockImage {
  url: string
  alt: string
  attribution: {
    photographer_name: string
    photographer_url: string
    source: string
  }
}

function invokeError(error: unknown, fallback: string): Error {
  if (error && typeof error === "object" && "message" in error) {
    return new Error(String((error as { message: unknown }).message) || fallback)
  }
  return new Error(fallback)
}

/** Search Unsplash via the proxy. Throws with a user-facing message on failure. */
export async function searchStockImages(
  orgId: string,
  query: string,
  page = 1
): Promise<StockSearchResult> {
  const { data, error } = await supabase.functions.invoke("stock-images", {
    body: { action: "search", org_id: orgId, query, page },
  })
  if (error) throw invokeError(error, "Stock image search failed")
  return data as StockSearchResult
}

/**
 * Ingest a selected photo into org storage and return the storage URL +
 * attribution. The returned url is the only thing callers should persist.
 */
export async function ingestStockImage(
  orgId: string,
  photo: StockPhoto
): Promise<IngestedStockImage> {
  const { data, error } = await supabase.functions.invoke("stock-images", {
    body: {
      action: "ingest",
      org_id: orgId,
      photo: {
        id: photo.id,
        download_location: photo.download_location,
        preview_url: photo.preview_url,
        alt: photo.alt,
        photographer_name: photo.photographer_name,
        photographer_url: photo.photographer_url,
      },
    },
  })
  if (error) throw invokeError(error, "Could not add the stock image")
  return data as IngestedStockImage
}
